/**
 * O DOSSIÊ DA LICITAÇÃO: leituras do bloco "Documentos gerados" da proposta.
 * Prévia aprovada: https://claude.ai/artifact/CRqh6PUDJHAykNjKHiBek2 (etapa 1).
 *
 * Respondem, sem abrir nada, a pergunta que importa com o relógio correndo: dá
 * para subir no portal?
 */

export interface DocDoDossie {
  id: number;
  name?: string | null;
  type?: string | null;
  status: string;
  clicksignDocumentKey?: string | null;
  packageDocumentId?: number | null;
}

export interface EstadoDoDossie {
  /** Documentos ativos (cancelado não conta). */
  total: number;
  assinados: number;
  aguardando: number;
  gerados: number;
  prontoParaPortal: boolean;
  /** 0..1, assinados / ativos. */
  progresso: number;
  texto: string;
}

/**
 * "10 assinados · pronto para o portal" ou "8 assinados · 2 aguardando".
 * Até 03/10/2026 a contagem só existia por grupo; o conjunto, que é o que vai
 * para o órgão, nunca era somado.
 */
export function estadoDoDossie(docs: DocDoDossie[]): EstadoDoDossie {
  const ativos = docs.filter((d) => d.status !== 'cancelado');
  const conta = (st: string) => ativos.filter((d) => d.status === st).length;
  const assinados = conta('assinado');
  const aguardando = conta('aguardando_assinatura');
  const gerados = ativos.length - assinados - aguardando;
  const prontoParaPortal = ativos.length > 0 && assinados === ativos.length;
  const partes = [`${assinados} ${assinados === 1 ? 'assinado' : 'assinados'}`];
  if (prontoParaPortal) partes.push('pronto para o portal');
  if (aguardando) partes.push(`${aguardando} aguardando`);
  if (gerados) partes.push(`${gerados} sem enviar`);
  return {
    total: ativos.length,
    assinados,
    aguardando,
    gerados,
    prontoParaPortal,
    progresso: ativos.length ? assinados / ativos.length : 0,
    texto: partes.join(' · '),
  };
}

/**
 * Os arquivos do "Baixar assinados (.zip)": o maço que sobe no anexo do portal.
 *
 * ⛔ DOCUMENTO DENTRO DE PACOTE NÃO ENTRA SOZINHO. Medido na proposta 371
 * (Dispensa 66/2026): as 8 declarações têm `packageDocumentId` = 290 e a MESMA
 * chave do ClickSign do pacote, então o "PDF assinado" de cada uma é o pacote
 * inteiro. Baixar documento por documento poria o mesmo arquivo 9 vezes no
 * zip. Entra o pacote; quem tem envelope próprio (a Proposta) entra sozinho.
 * Por segurança, também não repete a mesma chave do ClickSign.
 */
export function arquivosParaZip<T extends DocDoDossie>(docs: T[]): T[] {
  const assinados = docs.filter((d) => d.status === 'assinado');
  const pacotesAssinados = new Set(assinados.filter((d) => d.type === 'pacote_assinatura').map((d) => d.id));
  const chaves = new Set<string>();
  const out: T[] = [];
  // Pacotes primeiro: são eles que representam as chaves compartilhadas.
  const ordenados = [...assinados].sort((a, b) => Number(b.type === 'pacote_assinatura') - Number(a.type === 'pacote_assinatura'));
  for (const d of ordenados) {
    if (d.packageDocumentId != null && pacotesAssinados.has(d.packageDocumentId)) continue;
    const chave = d.clicksignDocumentKey || null;
    if (chave && chaves.has(chave)) continue;
    if (chave) chaves.add(chave);
    out.push(d);
  }
  // Devolve na ordem original da lista (Proposta antes do pacote).
  return docs.filter((d) => out.includes(d));
}

/** Nome do arquivo no zip, sem caracteres proibidos e sem repetir. */
export function nomesUnicos(nomes: string[]): string[] {
  const usados = new Map<string, number>();
  return nomes.map((n) => {
    const base = (n || 'documento').replace(/[\\/:*?"<>|]/g, '-').trim() || 'documento';
    const k = base.toLowerCase();
    const vezes = usados.get(k) || 0;
    usados.set(k, vezes + 1);
    return vezes === 0 ? `${base}_assinado.pdf` : `${base} (${vezes + 1})_assinado.pdf`;
  });
}

/**
 * O grupo começa recolhido? Grupo RESOLVIDO recolhe; grupo com pendência abre.
 *
 * Antes a regra `uniforme` só recolhia por volume (mais de 3 itens). Agora vale
 * para qualquer grupo inteiro num só estado RESOLVIDO (assinado ou cancelado).
 * Um grupo todo "gerado" ou todo "aguardando" é uniforme mas pede ação, e fica
 * aberto. Grupo de um documento só fica aberto: recolher não esconde nada e
 * custa um clique (é como a prévia desenha a Proposta).
 */
export function grupoComecaRecolhido(docs: DocDoDossie[]): boolean {
  if (docs.length < 2) return false;
  const st = docs[0].status;
  const uniforme = docs.every((d) => d.status === st);
  return uniforme && (st === 'assinado' || st === 'cancelado');
}

/** Quantos documentos um pacote reúne (para "8 documentos · 1 envelope ClickSign"). */
export const docsDoPacote = (pacote: DocDoDossie, docs: DocDoDossie[]) =>
  docs.filter((d) => d.packageDocumentId === pacote.id).length;

// ─── Etapa 4: signatários e histórico ──────────────────────────────────────

export interface SignatarioDoDoc {
  name: string;
  email?: string;
  cpf?: string;
  role?: string;
  signedAt?: string | null;
  clicksignSignerKey?: string | null;
}

/**
 * De onde ler os signatários de um documento.
 *
 * `signers` existe desde 02/10/2026 (migration DossieAssinaturaProcurement). É
 * NULL nas 112 linhas anteriores, de propósito: `[]` afirmaria "não teve
 * signatário", falso nos 84 assinados. Null = não sabemos, e a tela não
 * inventa. Pendente = AUSÊNCIA de `signedAt`; não há campo `status` nesta
 * forma, de propósito.
 *
 * ⛔ Documento de pacote tem a SUA lista: o backend copia os signatários do
 * envelope para cada filho no envio e na assinatura. Não ler do pacote.
 */
export function signatariosDe(d: any): SignatarioDoDoc[] | null {
  return Array.isArray(d?.signers) && d.signers.length > 0 ? d.signers : null;
}

/** Mesma regra do `linkDeAssinatura` de shared/DocumentViewerModal. */
export const linkDoSignatario = (s: SignatarioDoDoc): string | null =>
  s.clicksignSignerKey ? `https://app.clicksign.com/sign/${s.clicksignSignerKey}` : null;

/** Quem ainda não assinou e tem link próprio. */
export const pendentesComLink = (signers: SignatarioDoDoc[] | null) =>
  (signers || []).filter((s) => !s.signedAt && linkDoSignatario(s));

export const primeiroNome = (nome: string) => String(nome || '').trim().split(/\s+/)[0] || '—';

/** "aguardando Fábio" / "aguardando Fábio e Heitor" / "aguardando 3 signatários". */
export function quemFalta(signers: SignatarioDoDoc[] | null): string | null {
  const faltam = (signers || []).filter((s) => !s.signedAt);
  if (faltam.length === 0) return null;
  if (faltam.length <= 2) return `aguardando ${faltam.map((s) => primeiroNome(s.name)).join(' e ')}`;
  return `aguardando ${faltam.length} signatários`;
}

export interface EventoDoDoc {
  type: string;
  label: string;
  date: string;
  author?: string;
}

/**
 * Timeline mínima, montada só com o que a linha tem: criação, envio, cada
 * assinatura gravada e o `statusHistory`. Serve ao documento JÁ ASSINADO, que
 * é o estado em que ele passa o resto da vida, e foi exatamente aí que a tela
 * não dizia nada (o buraco que fez o DocumentViewerModal existir).
 *
 * Provisória: os Ofícios recebem a timeline pronta de `montarHistoricoDeAssinatura`
 * no backend (com os e-mails). Quando procurement ganhar a mesma rota
 * (`GET generated-documents/:id/history`, aguardando o ok do Heitor), ela
 * substitui esta função e os dois param de poder divergir.
 *
 * Como a função compartilhada: a trilha (`statusHistory`, rótulos humanos
 * 'Aguardando Assinatura' / 'Assinado') é a fonte preferida e as colunas
 * (`sentForSignatureAt`, `signedAt`) só reserva, para nunca sair linha dupla.
 * Ordena por data, não pela ordem de inserção.
 */
export function timelineMinima(d: any, signers: SignatarioDoDoc[] | null): EventoDoDoc[] {
  const ev: EventoDoDoc[] = [];
  const hist: any[] = Array.isArray(d?.statusHistory) ? d.statusHistory : [];
  const ehEnvio = (st: string) => /aguardando/i.test(st);
  const ehAssinado = (st: string) => /^assinado$/i.test(st);
  if (d?.createdAt) ev.push({ type: 'created', label: 'Documento gerado', date: d.createdAt });
  if (d?.sentForSignatureAt && !hist.some((h) => ehEnvio(h?.status || ''))) {
    ev.push({ type: 'sent', label: 'Enviado para assinatura', date: d.sentForSignatureAt, author: d.sentForSignatureBy || undefined });
  }
  for (const h of hist) {
    if (!h?.date) continue;
    const st = String(h.status || '');
    const label = ehEnvio(st) ? 'Enviado para assinatura' : ehAssinado(st) ? 'Assinatura concluída' : /cancel/i.test(st) ? 'Cancelado' : st;
    ev.push({
      type: ehEnvio(st) ? 'sent' : ehAssinado(st) ? 'signed' : 'status',
      label: h.notes ? `${label} · ${h.notes}` : label,
      date: h.date,
      author: h.changedBy || undefined,
    });
  }
  // Assinatura de cada pessoa só quando há mais de uma: com um signatário só,
  // ela É a conclusão, e as duas linhas diriam a mesma coisa.
  const lista = signers || [];
  if (lista.length > 1) {
    for (const s of lista) {
      if (s.signedAt) ev.push({ type: 'signer_signed', label: `${s.name} assinou`, date: s.signedAt });
    }
  }
  const concluidoNoHist = hist.some((h) => ehAssinado(h?.status || ''));
  if (d?.signedAt && !concluidoNoHist) {
    ev.push({ type: 'signed', label: 'Assinatura concluída', date: d.signedAt });
  }
  return ev.sort((x, y) => new Date(x.date).getTime() - new Date(y.date).getTime());
}
